/**
 * The Draft Planner on the site is the extension's planner component (planner-ui.js), but its plans are the same
 * ones as /planner on Showdown, saved in OgreClan's database. The two keep them in slightly different shapes; these
 * convert between them.
 *
 * - Showdown: { league, tab, view, source, plans: { [roomid or '']: { drafts, matchups, draft, matchup } },
 *   abilities: { [speciesid]: ability }, coverage, level, scarf, tailwind, trickRoom, move, group }
 * - The component: the same, but with abilities and "playing as" kept for each league ({ ...plans, abilities, me }),
 *   and version 2.
 */

export interface Draft { name: string, picks: string[] }
export interface Matchup { name: string, draft: string, week?: string, against?: string, theirs: string[] }
export interface ServerPlans { drafts: Draft[], matchups: Matchup[], draft: string, matchup: string }
export interface ServerState {
	league: string;
	tab: 'plan' | 'matchup';
	view?: string;
	source: string;
	plans: { [scope: string]: ServerPlans };
	abilities: { [speciesid: string]: string };
	coverage: string;
	level: number;
	scarf: boolean;
	tailwind: boolean;
	trickRoom: boolean;
	move: string;
	group: string;
	[other: string]: unknown;
}
export interface ComponentPlans extends ServerPlans { abilities: { [speciesid: string]: string }, me: string }
export interface ComponentState {
	version: 2;
	league: string;
	tab: 'plan' | 'matchup';
	source: string;
	plans: { [scope: string]: ComponentPlans };
	coverage: string;
	level: number;
	scarf: boolean;
	tailwind: boolean;
	trickRoom: boolean;
	move: string;
	group: string;
}

const DEFAULTS = {
	league: '', tab: 'plan' as const, view: 'team', source: 'roster', plans: {}, abilities: {},
	coverage: 'coverage', level: 50, scarf: false, tailwind: false, trickRoom: false, move: '', group: '',
};

/** Showdown saved one team of each before drafts and matchups had names; as /planner does, those become "Draft 1" and "Matchup 1" */
export function migrateServerState(raw: Record<string, unknown> | null): ServerState {
	const state = { ...DEFAULTS, ...raw } as ServerState & { you?: string[], opp?: string[], planned?: Record<string, string[]>, opponent?: Record<string, string> };
	if (raw && !raw.plans) {
		const plans: ServerState['plans'] = {};
		const scope = (key: string) => (plans[key] ||= { drafts: [], matchups: [], draft: '', matchup: '' });
		if (state.you?.length) scope('').drafts.push({ name: 'Draft 1', picks: state.you });
		if (state.opp?.length) scope('').matchups.push({ name: 'Matchup 1', draft: '', theirs: state.opp });
		for (const [league, picks] of Object.entries(state.planned || {})) {
			if (picks.length) scope(league).drafts.push({ name: 'Draft 1', picks });
		}
		for (const [league, against] of Object.entries(state.opponent || {})) {
			scope(league).matchups.push({ name: 'Matchup 1', draft: '', against, theirs: [] });
		}
		state.plans = plans;
	}
	delete state.you;
	delete state.opp;
	delete state.planned;
	delete state.opponent;
	return state;
}

export function toComponentState(raw: Record<string, unknown> | null, me: string): ComponentState {
	const server = migrateServerState(raw);
	const plans: ComponentState['plans'] = {};
	for (const [scope, p] of Object.entries(server.plans)) {
		plans[scope] = { ...p, abilities: { ...server.abilities }, me: scope ? me : '' };
	}
	return {
		version: 2, league: server.league, tab: server.tab,
		// Showdown can show a week's saved team instead of a draft, which the component doesn't do
		source: 'roster', plans,
		coverage: server.coverage, level: server.level, scarf: server.scarf, tailwind: server.tailwind, trickRoom: server.trickRoom,
		move: server.move, group: server.group,
	};
}

export function toServerState(component: ComponentState, previous: Record<string, unknown> | null): ServerState {
	const before = migrateServerState(previous);
	const abilities = { ...before.abilities };
	const plans: ServerState['plans'] = {};
	for (const [scope, p] of Object.entries(component.plans)) {
		const { abilities: chosen, me: _me, ...rest } = p;
		Object.assign(abilities, chosen);
		plans[scope] = rest;
	}
	return {
		...before,
		league: component.league, tab: component.tab, plans, abilities,
		// keep showing what Showdown showed, unless the site changed what it shows
		source: component.source === 'teambuilder' ? before.source : before.source.startsWith('week:') ? before.source : 'roster',
		coverage: component.coverage, level: component.level, scarf: component.scarf, tailwind: component.tailwind,
		trickRoom: component.trickRoom, move: component.move, group: component.group,
	};
}
